/** Reusable job-polling helper (FE contract §5.1).
 *
 *  Extracts the terminal-state `refetchInterval` pattern from
 *  `useOpenTofuStatus` (`useDeployments.ts`) so every catalog job type
 *  (generation now; metadata/eval later) shares one stop-set. Polling stops on
 *  terminal states AND on `preview_ready` (a RESTING state for
 *  catalog_generation — nothing changes until the admin commits).
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

/** Terminal job states — polling stops permanently here. */
export const CATALOG_JOB_TERMINAL = ['completed', 'failed', 'cancelled', 'expired'] as const;

// preview_ready is a resting state (poll stops until the admin commits).
const STOP_POLLING = new Set<string>([...CATALOG_JOB_TERMINAL, 'preview_ready']);

export function useJobPolling<T extends { status: string }>(
  key: readonly unknown[],
  queryFn: () => Promise<T>,
  opts?: { enabled?: boolean; intervalMs?: number },
): UseQueryResult<T> {
  return useQuery({
    queryKey: key,
    queryFn,
    enabled: opts?.enabled ?? true,
    staleTime: 0, // always fresh while polling
    refetchInterval: (q) => {
      const s = q.state.data?.status?.toLowerCase();
      if (!s || STOP_POLLING.has(s)) return false;
      return opts?.intervalMs ?? 2500;
    },
  });
}
